import { useState } from "react";
import { Send, CheckCircle, MapPin, Mail, Phone } from "lucide-react";

const FORMSPREE_ID = "xgorrwrg";

const serviceOptions = [
  "CIO as a Service",
  "IT Infrastructure Management",
  "ERP Operations & Management",
  "Cybersecurity & Compliance",
  "AI Services",
  "Multiple Services",
  "Not sure yet",
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service_interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSuccess(true);
        setForm({ name: "", email: "", company: "", phone: "", service_interest: "", message: "" });
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4">
              Contact Us
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-[#0A0A0A] mb-8">
              Let's start a conversation
            </h1>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              Whether you need strategic IT leadership, infrastructure management, or
              ERP solutions, our team is ready to help you transform technology into
              business value.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="pb-20 lg:pb-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-gray-200">
            {/* Form */}
            <div className="lg:col-span-2 p-8 lg:p-12 lg:border-r border-gray-200">
              {success ? (
                <div data-testid="contact-success-message" className="flex flex-col items-center justify-center h-full py-20">
                  <CheckCircle className="w-12 h-12 text-green-600 mb-6" />
                  <h3 className="font-heading text-2xl font-semibold text-[#0A0A0A] mb-3">
                    Message Sent
                  </h3>
                  <p className="text-sm text-[#4B5563] text-center max-w-sm">
                    Thank you for reaching out. Our team will get back to you within
                    24 business hours.
                  </p>
                  <button
                    data-testid="send-another-message-btn"
                    onClick={() => setSuccess(false)}
                    className="mt-8 px-6 py-2.5 border border-[#002FA7] text-[#002FA7] text-sm font-medium hover:bg-[#002FA7] hover:text-white transition-colors"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <h2 className="font-heading text-xl font-semibold text-[#0A0A0A] mb-8">
                    Send us a message
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-xs font-semibold text-[#0A0A0A] mb-2">
                        Full Name *
                      </label>
                      <input
                        data-testid="contact-name-input"
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 text-sm text-[#0A0A0A] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0A0A0A] mb-2">
                        Email Address *
                      </label>
                      <input
                        data-testid="contact-email-input"
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 text-sm text-[#0A0A0A] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                        placeholder="john@company.com"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0A0A0A] mb-2">
                        Company
                      </label>
                      <input
                        data-testid="contact-company-input"
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-200 text-sm text-[#0A0A0A] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                        placeholder="Your Company"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0A0A0A] mb-2">
                        Phone Number
                      </label>
                      <input
                        data-testid="contact-phone-input"
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-200 text-sm text-[#0A0A0A] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="block text-xs font-semibold text-[#0A0A0A] mb-2">
                      Service Interest
                    </label>
                    <select
                      data-testid="contact-service-select"
                      name="service_interest"
                      value={form.service_interest}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 text-sm text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent bg-white"
                    >
                      <option value="">Select a service</option>
                      {serviceOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-8">
                    <label className="block text-xs font-semibold text-[#0A0A0A] mb-2">
                      Message *
                    </label>
                    <textarea
                      data-testid="contact-message-textarea"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-200 text-sm text-[#0A0A0A] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent resize-none"
                      placeholder="Tell us about your project or how we can help..."
                    />
                  </div>
                  {error && (
                    <p data-testid="contact-error-message" className="text-sm text-red-600 mb-4">
                      {error}
                    </p>
                  )}
                  <button
                    data-testid="contact-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#002FA7] text-white text-sm font-medium hover:bg-[#001F70] transition-colors disabled:opacity-60"
                  >
                    {loading ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="p-8 lg:p-12 bg-[#F3F4F6]">
              <h3 className="font-heading text-lg font-semibold text-[#0A0A0A] mb-8">
                Contact Information
              </h3>
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <Mail className="w-5 h-5 text-[#002FA7] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-[#0A0A0A] mb-1">Email</p>
                    <p className="text-sm text-[#4B5563]">info@teqxl.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone className="w-5 h-5 text-[#002FA7] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-[#0A0A0A] mb-1">Phone (WhatsApp)</p>
                    <p className="text-sm text-[#4B5563]">+91 9920815906</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-[#002FA7] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-[#0A0A0A] mb-1">Location</p>
                    <p className="text-sm text-[#4B5563]">Mumbai, India</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-gray-300">
                <h4 className="text-xs tracking-[0.2em] uppercase font-bold text-[#4B5563] mb-4">
                  Office Hours
                </h4>
                <p className="text-sm text-[#4B5563] mb-1">Monday - Friday</p>
                <p className="text-sm text-[#0A0A0A] font-medium">9:00 AM - 6:00 PM IST</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
